import { useState } from 'react';
import { Check, Scale, Scissors, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { AnalyticalBalance, Crucible } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Weighing Tin Foil',
    content: 'Using an analytical balance ensures precise measurement of your starting material. The mass of tin is critical for calculating the empirical formula later. Record to three decimal places (0.001 g) for accuracy.'
  },
  2: {
    title: 'Breaking into Pieces',
    content: 'Small pieces of tin have greater surface area, allowing the nitric acid to react more completely and uniformly. Do not fold the tin - tearing creates irregular pieces that react better than smooth folded layers.'
  },
  3: {
    title: 'Adding to Crucible',
    content: 'Transfer all pieces carefully to avoid losing any sample. The crucible will contain your sample throughout the heating process. Any lost tin will result in calculation errors for the empirical formula.'
  }
};

export default function Part3TinPreparation() {
  const [tinMass, setTinMass] = useState<number | null>(null);
  const [tinBroken, setTinBroken] = useState(false);
  const [tinInCrucible, setTinInCrucible] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const handleWeighTin = () => {
    if (currentStep === 1) {
      // Target ~1.0g of tin
      const mass = 0.95 + Math.random() * 0.1;
      setTinMass(parseFloat(mass.toFixed(3)));
      setCurrentStep(2);
    }
  };

  const handleBreakTin = () => {
    if (tinMass && currentStep === 2) {
      setTinBroken(true);
      setCurrentStep(3);
    }
  };

  const handleAddToCrucible = () => {
    if (tinBroken && currentStep === 3) {
      setTinInCrucible(true);
    }
  };

  const completedCount = [tinMass, tinBroken, tinInCrucible].filter(Boolean).length;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Scale size={16} />
          <h3>Part III: Add Tin Foil</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Weigh Tin Foil', hint: 'Approximately 1.0 g', done: tinMass !== null },
            { id: 2, title: 'Break into Pieces', hint: 'Small pieces react better', done: tinBroken },
            { id: 3, title: 'Add to Crucible', hint: 'Transfer all pieces', done: tinInCrucible }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {tinMass && (
          <div className="data-panel">
            <h4>Recorded Data</h4>
            <div className="data-row">
              <span>Mass of Tin:</span>
              <span className="value">{tinMass.toFixed(3)} g</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Add Tin Foil"
        completedCount={completedCount}
        totalSteps={3}
        equipment={['Analytical balance', 'Tin foil', 'Crucible']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="tin-workspace">
          <div className="equipment-row">
            {/* Tin Foil Sheet */}
            <div
              className={`tin-container ${currentStep === 1 && !tinMass ? 'clickable' : ''}`}
              onClick={handleWeighTin}
            >
              <svg viewBox="0 0 80 80" width={80} height={80}>
                {!tinBroken ? (
                  <>
                    {/* Flat sheet */}
                    <rect x="15" y="25" width="50" height="35" fill="#C0C0C0" rx="2" />
                    <rect x="17" y="27" width="46" height="31" fill="#D8D8D8" />
                    {/* Shine effect */}
                    <line x1="20" y1="30" x2="55" y2="30" stroke="#F0F0F0" strokeWidth="2" />
                  </>
                ) : (
                  <>
                    {/* Broken pieces */}
                    <rect x="10" y="20" width="15" height="12" fill="#C0C0C0" transform="rotate(10 17 26)" />
                    <rect x="30" y="25" width="12" height="10" fill="#C8C8C8" transform="rotate(-5 36 30)" />
                    <rect x="48" y="22" width="18" height="14" fill="#B8B8B8" transform="rotate(8 57 29)" />
                    <rect x="15" y="40" width="14" height="11" fill="#D0D0D0" transform="rotate(-8 22 45)" />
                    <rect x="35" y="42" width="16" height="13" fill="#C4C4C4" transform="rotate(5 43 48)" />
                    <rect x="55" y="38" width="12" height="10" fill="#CCCCCC" transform="rotate(-3 61 43)" />
                  </>
                )}
              </svg>
              <span className="equipment-label">
                Tin Foil (Sn) {tinMass ? `${tinMass.toFixed(3)} g` : ''}
              </span>
            </div>

            {/* Break button */}
            {tinMass && !tinBroken && (
              <button
                className={`break-btn ${currentStep === 2 ? 'active' : ''}`}
                onClick={handleBreakTin}
              >
                <Scissors size={20} />
                Break into Pieces
              </button>
            )}

            {/* Analytical Balance */}
            <div className="balance-station">
              <AnalyticalBalance
                reading={tinMass || 0}
                stable={tinMass !== null}
                unit="g"
              />
              <span className="equipment-label">Analytical Balance</span>
            </div>

            {/* Crucible */}
            <div
              className={`crucible-container ${tinBroken && !tinInCrucible && currentStep === 3 ? 'clickable' : ''}`}
              onClick={handleAddToCrucible}
            >
              <Crucible
                hasContent={tinInCrucible}
                contentColor="#C0C0C0"
                temperature={25}
              />
              <span className="equipment-label">Crucible</span>
            </div>
          </div>

          {tinInCrucible && (
            <div className="status-message success">
              <Check size={20} />
              Tin added to crucible. Ready for acid addition.
            </div>
          )}

          <div className="info-box">
            <h4>Why Break the Tin?</h4>
            <p>
              Small pieces have more surface area, allowing the nitric acid to react
              more completely with the tin. Do not fold - breaking creates better pieces.
            </p>
          </div>
        </div>

        <style>{`
          .tin-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
          }

          .tin-container,
          .balance-station,
          .crucible-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 12px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .clickable {
            cursor: pointer;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 54, 0.4); }
            50% { box-shadow: 0 0 0 8px rgba(255, 90, 54, 0); }
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .break-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 8px;
            color: var(--text-primary);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .break-btn.active {
            border-color: var(--primary);
            animation: pulse 1.5s ease-in-out infinite;
          }

          .break-btn:hover {
            background: rgba(255, 90, 54, 0.1);
            border-color: var(--primary);
          }

          .data-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .data-panel h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--secondary);
          }

          .data-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
          }

          .data-row .value {
            font-family: monospace;
            font-weight: 600;
            color: var(--primary);
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .info-box {
            background: var(--bg-elevated);
            border-left: 3px solid var(--secondary);
            border-radius: 8px;
            padding: 16px;
            max-width: 400px;
          }

          .info-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
